import Grid from '@mui/material/Grid';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ASwitch } from '../../../common/inputs';
import { dayFormat } from '../../../core/constants';
import { IFormValidator } from '../../../core/FormValidator';
import { ResourceTextApplication } from '../../../core/resources';
import { FarmingTypeEnum, QuestionResourceText } from '../../../interfaces/enums';
import {
    IFarmQuestion,
    IPlanAnimalData,
    IPlannedAnimal,
    IProductionPlan,
    IProductionPlanAccess,
} from '../../../interfaces/IProductionPlan';
import EditProductionAnimalDataDialog from '../data-grid/EditProductionAnimalDataDialog';
import ProductionPlanAnimalsDataGrid from '../data-grid/ProductionPlanAnimalDataGrid';
import {
    BuildAnimalCapacities,
    BuildIntermediationAnimalSales,
    BuildIntermediationAnimalRequirements,
    BuildSlaughterAnimals,
    IProductionPlanDataGrid,
} from '../data-grid/ProductionPlanDataGrids';
import SectionTitle from '../data-grid/SectionTitle';
import AnimalCountMapper from '../helpers/data-grid-mappers/AnimalCounts';
import IntermedSalesMapper from '../helpers/data-grid-mappers/IntermediationSales';
import SlaughterSalesMapper from '../helpers/data-grid-mappers/SlaughterSales';
import { styled } from '@mui/material';
import Question from './Question/Question';

interface IProps {
    data: IPlanAnimalData;
    setData: (plan: IPlanAnimalData) => void;
    access: IProductionPlanAccess;
    readonly?: boolean;
    validator: IFormValidator;
    capacityError: boolean;
}

interface IEditDialogProps {
    title: string;
    data: IProductionPlanDataGrid;
    onSave: (newData: IProductionPlanDataGrid) => void;
}

const StyledGridContainer = styled(Grid)(({ theme }) => ({
    paddingTop: '20px',
}));

export default function AnimalDataGrids(props: IProps) {
    const { data, setData, access, readonly, validator, capacityError } = props;
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaLayout',
        'AnelmaCattleProductionPlan',
    ]);

    const [showTransmissionSales] = useState<boolean>(
        access.HasDairyAnimals ||
            access.HasBreedingAnimals ||
            !!access.ProductionDirection.find((x) => x === FarmingTypeEnum.Transmission) ||
            !!access.ProductionDirection.find((x) => x === FarmingTypeEnum.Dairy)
    );

    const [showTransmissionReq] = useState<boolean>(
        !!access.ProductionDirection.find((x) => x === FarmingTypeEnum.Transmission) ||
            !!access.ProductionDirection.find((x) => x === FarmingTypeEnum.Upbringing)
    );

    const [showSlaughter] = useState<boolean>(
        !!access.ProductionDirection.find((x) => x === FarmingTypeEnum.Dairy) ||
            !!access.ProductionDirection.find(
                (x) =>
                    x === FarmingTypeEnum.Upbringing ||
                    !!access.ProductionDirection.find((x) => x === FarmingTypeEnum.Calving)
            )
    );

    const [showRegisterDate, setShowRegisterDate] = useState<boolean>(false);

    const answerIsTrue = (questionResource: QuestionResourceText) => {
        let qi = data.Questions.findIndex(q =>
            q.ResourceId === questionResource.toString());

        return qi !== -1 && data.Questions[qi].Value === '1';
    }

    const [showTransmissionSalesDataGrid, setShowTransmissionSalesDataGrid] = useState<boolean>(
        !answerIsTrue(QuestionResourceText.NoTransmissionBovineSold)
    );

    const [showSlaughterSoldDataGrid, setShowSlaughterSoldDataGrid] = useState<boolean>(
        !answerIsTrue(QuestionResourceText.NoSlaughterBovineSold)
    );

    // parsed data grid data
    const [animalCapacities, setAnimalCapacities] = useState<IProductionPlanDataGrid>();
    const [intermedSales, setIntermedSales] = useState<IProductionPlanDataGrid>();
    const [intermedReq, setIntermedReq] = useState<IProductionPlanDataGrid>();
    const [slaughter, setSlaughter] = useState<IProductionPlanDataGrid>();

    const [editData, setEditData] = useState<IEditDialogProps>();

    const getAnimalCapacities = (all?: boolean) =>
        BuildAnimalCapacities(access, data.AnimalCapacities, all);

    const getIntermediationAnimalSales = (all?: boolean) =>
        BuildIntermediationAnimalSales(
            access,
            data.PreviousTransmissionAnimalsSold,
            data.PlannedTransmissionAnimalsSold,
            all
        );

    const getIntermediationAnimalRequirements = (all?: boolean) =>
        BuildIntermediationAnimalRequirements(
            access,
            data.PreviousTransmissionAnimalsNeeded,
            data.PlannedTransmissionAnimalsNeeded,
            all
        );

    const getSlaughterAnimals = (all?: boolean) =>
        BuildSlaughterAnimals(
            access,
            data.PreviousSlaughterBovineSold,
            data.PlannedSlaughterBovineSold,
            all
        );

    useEffect(() => {
        setShowTransmissionSalesDataGrid(!answerIsTrue(QuestionResourceText.NoTransmissionBovineSold));

        if (answerIsTrue(QuestionResourceText.NoTransmissionBovineSold))
            setPlannedTransmissionSalesValuesToZero();

        setShowSlaughterSoldDataGrid(!answerIsTrue(QuestionResourceText.NoSlaughterBovineSold));

        if (answerIsTrue(QuestionResourceText.NoSlaughterBovineSold))
            setPlannedSlaughterSoldValuesToZero();
    }, [data.Questions]);

    useEffect(() => {
        setAnimalCapacities(getAnimalCapacities());
    }, [data.AnimalCapacities])

    useEffect(() => {
        setAnimalCapacities(getAnimalCapacities());
        setIntermedSales(getIntermediationAnimalSales());
        setIntermedReq(getIntermediationAnimalRequirements());
        setSlaughter(getSlaughterAnimals());
    }, [
        data.AnimalCapacities,
        data.PreviousTransmissionAnimalsSold,
        data.PlannedTransmissionAnimalsSold,
        data.PreviousTransmissionAnimalsNeeded,
        data.PlannedTransmissionAnimalsNeeded,
        data.PreviousSlaughterBovineSold,
        data.PlannedSlaughterBovineSold,
    ]);

    useEffect(() => {
        setShowRegisterDate(!data.AnimalCapacities.find((x) => x.Animals > 0 && x.IsPlanned));
    }, [animalCapacities]);

    const setQuestionValue = (questionResourceId: QuestionResourceText, val: boolean) => {
        let yes = '1';
        let no = '2';

        let qi = data.Questions.findIndex(q => q.ResourceId === questionResourceId.toString());
        if (qi != -1) {
            let q = data.Questions[qi];
            q.Value = val ? yes : no;
            setData({ ...data, Questions: [q, ...data.Questions.filter(q => q.ResourceId !== questionResourceId.toString())] });
        }
    }

    const getQuestion = (question: IFarmQuestion, error?: boolean, required?: boolean, disabled?: boolean) => {
        return (
            <Question
                question={question}
                setQuestion={(question) => {
                    setData({
                        ...data,
                        Questions: [
                            question,
                            ...data.Questions.filter(q => q.ResourceId !== question.ResourceId)
                        ],
                    });
                }}
                validator={validator}
                readonly={readonly || disabled}
                error={error}
                required={required}
            />
        );
    }

    const getQuestionElement = (questionResourceId: QuestionResourceText, disabled: boolean) => {
        let i = data.Questions.findIndex(q => q.ResourceId === questionResourceId.toString());

        if (i != -1) {
            return getQuestion(data.Questions[i], undefined, undefined, disabled);
        }

        return <></>;
    }

    const onAnimalCapacitiesEdit = () => {
        setEditData({
            title: t('AnelmaLayout:1069'),
            data: getAnimalCapacities(true),
            onSave: (newData: IProductionPlanDataGrid) => {
                let capacities = [...data.AnimalCapacities];
                newData.rows.forEach((row) => {
                    AnimalCountMapper.ToArray(capacities, row);
                });

                setData({ ...data, AnimalCapacities: capacities });
            },
        });
    };

    const onIntermedSalesEdit = () => {
        setEditData({
            title: t('AnelmaLayout:1066'),
            data: getIntermediationAnimalSales(true),
            onSave: (newData: IProductionPlanDataGrid) => {
                newData.rows.forEach((row) => {
                    IntermedSalesMapper.ToPlan(data, row.collection, row);
                });
                setIntermedSales(getIntermediationAnimalSales());
                setQuestionValue(QuestionResourceText.NoTransmissionBovineSold, false);
                setShowTransmissionSalesDataGrid(true);
            },
        });
    };

    const onIntermedReqEdit = () => {
        setEditData({
            title: t('AnelmaLayout:1067'),
            data: getIntermediationAnimalRequirements(true),
            onSave: (newData: IProductionPlanDataGrid) => {
                newData.rows.forEach((row) => {
                    IntermedSalesMapper.ToPlan(data, row.collection, row);
                });
                setIntermedReq(getIntermediationAnimalRequirements());
            },
        });
    };

    const onSlaughterSalesEdit = () => {
        setEditData({
            title: t('AnelmaLayout:1068'),
            data: getSlaughterAnimals(),
            onSave: (newData: IProductionPlanDataGrid) => {
                newData.rows.forEach((row) => {
                    SlaughterSalesMapper.ToPlan(data, row.collection, row);
                });
                setSlaughter(getSlaughterAnimals());
                setQuestionValue(QuestionResourceText.NoSlaughterBovineSold, false);
                setShowSlaughterSoldDataGrid(true);
            },
        });
    };

    const setPlannedTransmissionSalesValuesToZero = () => {
        let zeroPlannedValues = { ...intermedSales };
        if (zeroPlannedValues.rows && zeroPlannedValues.columns) {
            zeroPlannedValues.rows[1].ColostrumCalfBulls = 0;
            zeroPlannedValues.rows[1].ColostrumCalfCows = 0;
            zeroPlannedValues.rows[1].IntermediationBulls = 0;
            zeroPlannedValues.rows[1].IntermediationCows = 0;
            zeroPlannedValues.rows[1].BeefCalfBulls = 0;
            zeroPlannedValues.rows[1].BeefCalfCows = 0;
            zeroPlannedValues.rows[1].VkBreederCalfBulls = 0;
            zeroPlannedValues.rows[1].VkBreederCalfCows = 0;
            setIntermedSales(zeroPlannedValues as IProductionPlanDataGrid);
        }
        for (let i = 0; i < data.PlannedTransmissionAnimalsSold.length; i++) {
            data.PlannedTransmissionAnimalsSold[i].Amount = 0;
        }
    };

    const setPlannedSlaughterSoldValuesToZero = () => {
        let zeroPlannedValues = { ...slaughter };
        if (zeroPlannedValues.rows && zeroPlannedValues.columns) {
            zeroPlannedValues.rows[1].Cows = 0;
            zeroPlannedValues.rows[1].Bulls = 0;
            zeroPlannedValues.rows[1].Heifers = 0;
            zeroPlannedValues.rows[1].BeefCalfBulls = 0;
            zeroPlannedValues.rows[1].BeefCalfCows = 0;
            zeroPlannedValues.rows[1].ColostrumCalfBulls = 0;
            zeroPlannedValues.rows[1].ColostrumCalfCows = 0;
            zeroPlannedValues.rows[1].IntermediationBulls = 0;
            zeroPlannedValues.rows[1].IntermediationCows = 0;
            zeroPlannedValues.rows[1].VkBreederCalfBulls = 0;
            zeroPlannedValues.rows[1].VkBreederCalfCows = 0;
            zeroPlannedValues.rows[1].SlaughterBeefHeifer = 0;
            zeroPlannedValues.rows[1].SlaughterBeefBull = 0;
            setSlaughter(zeroPlannedValues as IProductionPlanDataGrid);
        }
        for (let i = 0; i < data.PlannedSlaughterBovineSold.length; i++) {
            data.PlannedSlaughterBovineSold[i].Amount = 0;
        }
    };

    const disableSwitch = (plannedAnimals: IPlannedAnimal[]) => {
        return plannedAnimals.find((_) => _.Amount !== 0 && _.Amount !== null) ? true : false;
    };

    return (
        <>
            <Grid container direction='row' sx={{ paddingLeft: '40px' }}>
                <StyledGridContainer
                    container
                    style={{ border: capacityError ? 'solid red' : 'none' }}
                >
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                        <SectionTitle
                            title={t('AnelmaLayout:1069')}
                            readonly={readonly}
                            onEdit={onAnimalCapacitiesEdit}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                        {showRegisterDate && data.BovineRegisterDate && (
                            <span>
                                {`${t('AnelmaCattleProductionPlan:1063')} ${moment(
                                    data.BovineRegisterDate
                                ).format(dayFormat)}`}
                            </span>
                        )}
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <ProductionPlanAnimalsDataGrid data={animalCapacities} />
                        </Grid>
                    </Grid>
                </StyledGridContainer>

                {showTransmissionSales && (
                    <StyledGridContainer container>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <SectionTitle
                                title={t('AnelmaLayout:1066')}
                                readonly={showTransmissionSalesDataGrid ? readonly : !readonly}
                                onEdit={onIntermedSalesEdit}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            {showTransmissionSalesDataGrid && (
                                <ProductionPlanAnimalsDataGrid data={intermedSales} />
                            )}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            {getQuestionElement(QuestionResourceText.NoTransmissionBovineSold, disableSwitch(data.PlannedTransmissionAnimalsSold))}
                        </Grid>
                    </StyledGridContainer>
                )}

                {showTransmissionReq && (
                    <StyledGridContainer container>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <SectionTitle
                                title={t('AnelmaLayout:1067')}
                                readonly={readonly}
                                onEdit={onIntermedReqEdit}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <ProductionPlanAnimalsDataGrid data={intermedReq} />
                        </Grid>
                    </StyledGridContainer>
                )}

                {showSlaughter && (
                    <StyledGridContainer container>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <SectionTitle
                                title={t('AnelmaLayout:1068')}
                                readonly={showSlaughterSoldDataGrid ? readonly : !readonly}
                                onEdit={onSlaughterSalesEdit}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            {showSlaughterSoldDataGrid && (
                                <ProductionPlanAnimalsDataGrid data={slaughter} />
                            )}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            {getQuestionElement(QuestionResourceText.NoSlaughterBovineSold, disableSwitch(data.PlannedSlaughterBovineSold))}
                        </Grid>
                    </StyledGridContainer>
                )}
            </Grid>
            {editData && !readonly && (
                <EditProductionAnimalDataDialog
                    title={editData.title}
                    data={editData.data}
                    onSave={editData.onSave}
                    onClose={() => setEditData(undefined)}
                />
            )}
        </>
    );
}
